if (!customElements.get('custom-select-me')) {
  class CustomSelectMe extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });

      const style = document.createElement('style');
      style.textContent = `
        :host {
          --select-padding: 14px 12px;
          --option-padding: 15px 25px;
          --no-match-padding: 15px 25px;
          --clear-btn-padding: 4px;
          /* ===== hover 默认颜色（已存在语义） ===== */
          --option-hover-color: #333;
          --option-hover-bg-color: #fafafa;

          /* ===== 当前选中 option 的颜色（默认=hover） ===== */
          --select-color: var(--option-hover-color);
          --select-bg-color: var(--option-hover-bg-color);
        }
        .select-container { position: relative; width: 100%; }
        .selected {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-weight: 400;
          border: 1px solid #ccc;
          border-radius: 8px;
          background-color: white;
          font-size: 1em;
          user-select: none;
        }
        .selected:hover, .selected:focus { border: 1px solid #333333; }
        .selected.error { border: 1px solid #e53935 !important; background-color: #fff0f0; }
        .select-input {
          padding: var(--select-padding);
          border: none;
          border-radius: 8px;
          font-size: 1em;
          width: 100%;
          line-height: 1.2em;
          background: transparent;
          flex: 1;
        }
        .select-input:focus { outline: none; }
        .arrow {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          margin-right: 12px;
          transition: transform 0.3s ease;
        }
        .open .arrow { transform: rotate(180deg); }
        .clear-btn {
          display: none;
          align-items: center;
          justify-content: center;
          margin-right: 6px;
          cursor: pointer;
          border-radius: 50%;
        }
        .clear-btn:hover { background-color: #dfdfdf; }
        .clear-btn svg { width: 21px; height: auto; pointer-events: none; }
        .options {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
          background: white;
          display: none;
          z-index: 10;
          border-radius: 0 0 8px 8px;
          max-height: 300px;
          overflow-y: auto;
        }
        .open .options { display: block; }
        .option { padding: var(--option-padding); cursor: pointer; color: #999; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;}
        .option:hover { background-color: #fafafa; color: #333; font-weight: 500; }
        .option.selected-option {
          color: var(--select-color);
          background-color: var(--select-bg-color);
          font-weight: 500;
        }
        .option.selected-option:hover {
          color: var(--select-color);
          background-color: var(--select-bg-color);
        }
        /* ✅ disabled option style */
        .option.disabled { color: #ccc; cursor: not-allowed; font-weight: 400; }
        .option.disabled:hover { background-color: transparent; color: #ccc; font-weight: 400; }

        .no-match {
          padding: var(--no-match-padding);
          color: #ccc;
          text-align: center;
          font-style: italic;
        }
      `;

      this.container = document.createElement('div');
      this.container.classList.add('select-container');
      this.container.innerHTML = `
        <div class="selected">
          <input class="select-input" placeholder="${this.getAttribute('placeholder') || 'Search or select'}" />
          <span class="clear-btn" title="Clear selection">
            <svg width="21px" height="21px" viewBox="0 0 21 21">
              <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                <g transform="translate(-1551.000000, -171.000000)">
                  <g transform="translate(1551.000000, 171.000000)">
                    <rect x="0" y="0" width="21" height="21"></rect>
                    <line x1="14.3" y1="7" x2="7" y2="14.3" stroke="#333333" stroke-linecap="round" stroke-linejoin="round"></line>
                    <line x1="14.3" y1="7" x2="7" y2="14.3" stroke="#333333" stroke-linecap="round" stroke-linejoin="round" transform="translate(10.65, 10.65) scale(-1, 1) translate(-10.65, -10.65)"></line>
                  </g>
                </g>
              </g>
            </svg>
          </span>
          <span class="arrow">
            <svg width="24" height="24" viewBox="0 0 24 24">
              <path d="M20 8.5 12.5 16 5 8.5" stroke="currentColor" stroke-width="1.5" fill="none"/>
            </svg>
          </span>
        </div>
        <div class="options"></div>
      `;

      this.shadowRoot.append(style, this.container);

      this._observer = null;
      this._scheduled = false;
    }
    /*
      data-all-flag="true"
      启用 “All” 行为：空值时展示 All，clear 时回到 All（非 required 才生效）。

      data-required="true"
      必填：不显示清除按钮；空值/非法/被禁用时自动回落到第一个可用 option。

      data-selected="true" ✅（本次新增逻辑）
      搜索输入时：不隐藏未匹配项，而是全量显示并把匹配项置顶（稳定排序）。
    */
    // ✅ 统一读取 data 配置（整合所有 data-* 的作用）
    _getConfig() {
      return {
        allFlag: this.getAttribute('data-all-flag') === 'true',
        required: this.getAttribute('data-required') === 'true',
        // ✅ data-selected="true"：搜索时不只显示匹配项，而是全量显示 + 匹配置顶（稳定排序）
        sortOnSearch: this.getAttribute('data-selected') === 'true',
      };
    }

    _getOptions() {
      return Array.from(this.querySelectorAll('option'));
    }

    _isDisabled(opt) {
      return opt.disabled || opt.hasAttribute('disabled');
    }

    _getFirstEnabledOption() {
      return this._getOptions().find(o => !this._isDisabled(o));
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      this._scheduled = false;
    }

    connectedCallback() {
      const input = this.container.querySelector('.select-input');
      const optionsDiv = this.container.querySelector('.options');
      const selectedDiv = this.container.querySelector('.selected');
      const clearBtn = this.container.querySelector('.clear-btn');

      const cfg = this._getConfig();

      const updateClearBtnVisibility = () => {
        if (cfg.required) {
          clearBtn.style.display = 'none';
          return;
        }
        clearBtn.style.display = this.getAttribute('value') ? 'inline-flex' : 'none';
      };

      const emitOptionChange = (value, label) => {
        this.dispatchEvent(new Event('change'));
        this.dispatchEvent(new CustomEvent('option-change', {
          detail: { value, label },
          bubbles: true,
          composed: true
        }));
      };

      // ✅ 核心：renderOptions 支持 “匹配置顶但不隐藏其它项”
      const renderOptions = (filterRaw = '') => {
        let filter = filterRaw;
        if (filter === 'All' && cfg.allFlag) filter = '';

        const options = this._getOptions();
        const f = filter.trim().toLowerCase();

        optionsDiv.innerHTML = '';

        // 匹配判断（用 includes）
        const isMatch = (opt) => opt.textContent.toLowerCase().includes(f);

        let list = options;

        // ✅ 当输入有 filter：
        // - sortOnSearch=true => 全量显示 + 匹配项置顶（稳定分组）
        // - sortOnSearch=false => 沿用你原来的“只显示匹配项”
        if (f) {
          if (cfg.sortOnSearch) {
            const matched = [];
            const rest = [];
            for (const opt of options) {
              (isMatch(opt) ? matched : rest).push(opt);
            }
            list = matched.concat(rest);
          } else {
            list = options.filter(isMatch);
          }
        }

        // 只有在“只显示匹配项”模式下，才可能出现无匹配需要 no-match
        if (list.length === 0) {
          const noMatch = document.createElement('div');
          noMatch.className = 'no-match';
          noMatch.textContent = "No matching options";
          optionsDiv.appendChild(noMatch);
          return;
        }

        list.forEach(opt => {
          const div = document.createElement('div');
          div.className = 'option';
          div.textContent = opt.textContent;
          div.dataset.value = opt.value;
          // ✅ 当前选中项高亮
          if (this.getAttribute('value') === opt.value) {
            div.classList.add('selected-option');
          }
          const disabled = this._isDisabled(opt);
          if (disabled) {
            div.classList.add('disabled');
            div.setAttribute('aria-disabled', 'true');
          } else {
            div.addEventListener('mousedown', () => {
              input.value = opt.textContent;
              this.setAttribute('value', opt.value);
              this.container.classList.remove('open');
              updateClearBtnVisibility();
              emitOptionChange(opt.value, opt.textContent);
            });
          }

          optionsDiv.appendChild(div);
        });
      };

      const applyRequiredDefaultIfNeeded = () => {
        if (!cfg.required) return;

        const currentVal = this.getAttribute('value');
        const opts = this._getOptions();
        const currentOpt = currentVal ? opts.find(o => o.value === currentVal) : null;

        if (!currentOpt || this._isDisabled(currentOpt)) {
          const first = this._getFirstEnabledOption();
          if (first) {
            input.value = first.textContent;
            this.setAttribute('value', first.value);
            emitOptionChange(first.value, first.textContent);
          } else {
            input.value = '';
            this.removeAttribute('value');
            emitOptionChange('', '');
          }
        }
      };

      input.addEventListener('focus', () => {
        this.container.classList.add('open');
        const val = input.value.trim().toLowerCase();
        if (cfg.allFlag && val === 'all') {
          input.value = '';
          renderOptions('');
        } else {
          renderOptions(input.value);
        }
      });

      selectedDiv.addEventListener('pointerdown', (e) => {
        if (e.target !== input) {
          e.preventDefault();
          setTimeout(() => input.focus(), 0);
        }
      });

      input.addEventListener('input', () => {
        renderOptions(input.value);
        updateClearBtnVisibility();
      });

      input.addEventListener('blur', () => {
        setTimeout(() => {
          const inputVal = input.value.trim().toLowerCase();
          const options = this._getOptions();

          // ✅ 只匹配“启用项”
          const matchedEnabled = options.some(opt =>
            !this._isDisabled(opt) && opt.textContent.toLowerCase() === inputVal
          );

          this.container.classList.remove('open');

          if (!matchedEnabled) {
            if (cfg.required) {
              const first = this._getFirstEnabledOption();
              if (first) {
                input.value = first.textContent;
                this.setAttribute('value', first.value);
                emitOptionChange(first.value, first.textContent);
              } else {
                input.value = '';
                this.removeAttribute('value');
                emitOptionChange('', '');
              }
              updateClearBtnVisibility();
              return;
            }

            if (cfg.allFlag) {
              input.value = 'All';
              this.removeAttribute('value');
              emitOptionChange('', 'All');
            } else {
              input.value = '';
              this.removeAttribute('value');
              emitOptionChange('', '');
            }
            updateClearBtnVisibility();
          }
        }, 150);
      });

      clearBtn.addEventListener('click', (e) => {
        e.stopPropagation();

        if (cfg.required) {
          const first = this._getFirstEnabledOption();
          if (first) {
            input.value = first.textContent;
            this.setAttribute('value', first.value);
            emitOptionChange(first.value, first.textContent);
          } else {
            input.value = '';
            this.removeAttribute('value');
            emitOptionChange('', '');
          }
          updateClearBtnVisibility();
          renderOptions('');
          return;
        }

        if (cfg.allFlag) {
          input.value = 'All';
          this.removeAttribute('value');
          emitOptionChange('', 'All');
        } else {
          input.value = '';
          this.removeAttribute('value');
          emitOptionChange('', '');
        }

        updateClearBtnVisibility();
        renderOptions('');
      });

      // ===== 初始化 =====
      if (!this.getAttribute('value') && cfg.required) {
        const first = this._getFirstEnabledOption();
        if (first) {
          input.value = first.textContent;
          this.setAttribute('value', first.value);
          emitOptionChange(first.value, first.textContent);
        } else {
          input.value = '';
          this.removeAttribute('value');
          emitOptionChange('', '');
        }
      }

      if (!this.getAttribute('value') && cfg.allFlag && !cfg.required) {
        input.value = 'All';
        emitOptionChange('', 'All');
      }
      if (this.getAttribute('value')) {
        const currentVal = this.getAttribute('value');
        const options = this._getOptions();
        const currentOpt = options.find(opt => opt.value === currentVal);
      
        if (currentOpt && !this._isDisabled(currentOpt)) {
          input.value = currentOpt.textContent;
        }
      }

      renderOptions('');
      updateClearBtnVisibility();
      applyRequiredDefaultIfNeeded();

      // ===== 只监听 disabled 变化 =====
      if (this._observer) this._observer.disconnect();

      const scheduleRefresh = () => {
        if (this._scheduled) return;
        this._scheduled = true;
        requestAnimationFrame(() => {
          this._scheduled = false;

          renderOptions(input.value);

          const currentVal = this.getAttribute('value');
          if (currentVal) {
            const options = this._getOptions();
            const opt = options.find(o => o.value === currentVal);
            const disabled = !opt || this._isDisabled(opt);

            if (disabled) {
              if (cfg.required) {
                const first = this._getFirstEnabledOption();
                if (first) {
                  input.value = first.textContent;
                  this.setAttribute('value', first.value);
                  emitOptionChange(first.value, first.textContent);
                } else {
                  input.value = '';
                  this.removeAttribute('value');
                  emitOptionChange('', '');
                }
              } else if (cfg.allFlag) {
                input.value = 'All';
                this.removeAttribute('value');
                emitOptionChange('', 'All');
              } else {
                input.value = '';
                this.removeAttribute('value');
                emitOptionChange('', '');
              }
              updateClearBtnVisibility();
            }
          } else {
            if (cfg.required) {
              const first = this._getFirstEnabledOption();
              if (first) {
                input.value = first.textContent;
                this.setAttribute('value', first.value);
                emitOptionChange(first.value, first.textContent);
                updateClearBtnVisibility();
              }
            }
          }
        });
      };

      this._observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'attributes' && m.attributeName === 'disabled') {
            scheduleRefresh();
            break;
          }
        }
      });

      this._observer.observe(this, {
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled']
      });
    }

    setErrorState(hasError) {
      const selectedEl = this.container.querySelector('.selected');
      if (selectedEl) selectedEl.classList.toggle('error', hasError);
    }

    get value() {
      return this.getAttribute('value');
    }

    set value(val) {
      const input = this.container.querySelector('.select-input');
      const cfg = this._getConfig();

      const options = this._getOptions();

      const getFirstEnabledOption = () => {
        return options.find(o => !(o.disabled || o.hasAttribute('disabled')));
      };

      const emitOptionChange = (value, label) => {
        this.dispatchEvent(new Event('change'));
        this.dispatchEvent(new CustomEvent('option-change', {
          detail: { value, label },
          bubbles: true,
          composed: true
        }));
      };

      if (!val) {
        if (cfg.required) {
          const first = getFirstEnabledOption();
          if (first) {
            input.value = first.textContent;
            this.setAttribute('value', first.value);
            emitOptionChange(first.value, first.textContent);
          } else {
            input.value = '';
            this.removeAttribute('value');
            emitOptionChange('', '');
          }
          return;
        }

        if (cfg.allFlag) {
          input.value = 'All';
          this.removeAttribute('value');
          emitOptionChange('', 'All');
          return;
        }

        input.value = '';
        this.removeAttribute('value');
        emitOptionChange('', '');
        return;
      }

      const opt = options.find(o => o.value === val);
      const disabled = !opt || opt.disabled || opt.hasAttribute('disabled');

      if (!disabled) {
        input.value = opt.textContent;
        this.setAttribute('value', val);
        emitOptionChange(val, opt.textContent);
      } else {
        if (cfg.required) {
          const first = getFirstEnabledOption();
          if (first) {
            input.value = first.textContent;
            this.setAttribute('value', first.value);
            emitOptionChange(first.value, first.textContent);
          } else {
            input.value = '';
            this.removeAttribute('value');
            emitOptionChange('', '');
          }
        } else if (cfg.allFlag) {
          input.value = 'All';
          this.removeAttribute('value');
          emitOptionChange('', 'All');
        } else {
          input.value = '';
          this.removeAttribute('value');
          emitOptionChange('', '');
        }
      }
    }
  }

  customElements.define('custom-select-me', CustomSelectMe);
}
